// advancedSection/LogsDeAuditoria.tsx
//
// A sub-tela `Avançado › Logs`: quem fez o quê, em quê, quando e em qual loja.
//
// POR QUE ESTE ARQUIVO É UM COMPONENTE SEPARADO, e não mais um `case` dentro do
// `AdvancedSection`: aquele arquivo é um `switch` direto no `return`, sem
// nenhum hook. Não é possível pôr `useState`/`useEffect` dentro de um `case` —
// as Regras dos Hooks proíbem chamada condicional. A tela que faz `GET` tem de
// morar num componente próprio, como `comunicacoesSection/LogDeEnvio.tsx` já é.
//
// DE ONDE O DADO VEM: `GET /auditoria`, paginado, SOMENTE ADMINISTRADOR. Não
// vem do InfoToken, de propósito — foi um `subsidiary.logs.map(...)` sobre uma
// chave que nunca existiu no token que deixava esta tela BRANCA por meses. Log
// paginado não cabe num JWT que viaja em todo cabeçalho.
//
// AS SITUAÇÕES, e por que cada uma tem texto próprio:
//   carregando  — a tela busca na rede; sem este estado a foto do gabarito e o
//                 olho do usuário veriam "vazio" antes da resposta chegar.
//   403         — NÃO é falha. É a regra: log mostra quem fez o quê, e o
//                 backend só o abre para administrador. Dizer "não foi possível
//                 carregar" aqui faria o usuário achar que quebrou.
//   vazio       — e o POR QUÊ junto. A lista de ações cobertas vem do SERVIDOR
//                 (`cobertura`), nunca escrita à mão aqui: assim ela não pode
//                 mentir sobre o que o backend instrumentou. Sem esse bloco, um
//                 dono leria a tabela vazia como "nada aconteceu no sistema",
//                 quando a verdade pode ser "isto ainda não é registrado".
//   ESTRUTURA   — vazio por falta de linha e vazio por falta de TABELA não são a
//   AUSENTE       mesma coisa, e chegavam aqui iguais. `app.ts` tolera de
//                 propósito o schema `alog` falhar no boot (o Central sobe sem
//                 log em vez de não subir), e nesse caso o texto "o log passou a
//                 existir agora" seria falso: nada está sendo gravado. O
//                 servidor passou a dizer qual dos dois é (`estrutura`).
//   FILTRO       — filtro que o servidor não conseguiu usar vira aviso, porque a
//   IGNORADO      lista mostrada não é o recorte que foi pedido. Descartar em
//                 silêncio é a tela mentir por omissão.
//   tabela      — o log de verdade, paginado.
//
// NADA DE DADO DE EXEMPLO. Nenhum mock, nenhuma linha ilustrativa. Se está
// vazio, está vazio — foi essa a regra do card que entregou o estado vazio
// honesto, e ela continua valendo agora que o log existe.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/sections/advancedSection";
import auditoriaService, {
  EstruturaDaTrilha,
  EventoDeAuditoria,
  ItemDaCobertura,
  ROTULO_DA_ACAO,
  rotuloDaEntidade,
} from "../../../../services/auditoria.service";
import { comoDataHora } from "../../../../services/compras.service";

interface LogsDeAuditoriaProps {
  decryptedInfoToken: any;
}

const POR_PAGINA = 20;

const LogsDeAuditoria: React.FC<LogsDeAuditoriaProps> = ({
  decryptedInfoToken,
}) => {
  const [eventos, setEventos] = useState<EventoDeAuditoria[]>([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [cobertura, setCobertura] = useState<ItemDaCobertura[]>([]);
  const [desde, setDesde] = useState<string | null>(null);
  // `undefined` = servidor antigo, que ainda não manda o campo. Nesse caso a
  // tela se comporta como antes, em vez de acusar uma ausência que não sabe.
  const [estrutura, setEstrutura] = useState<EstruturaDaTrilha | undefined>(
    undefined
  );
  const [filtrosIgnorados, setFiltrosIgnorados] = useState<string[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [semPermissao, setSemPermissao] = useState(false);

  const carregar = useCallback((paginaAlvo: number) => {
    // Guarda de unmount: sem ela, trocar de aba enquanto a resposta está no ar
    // faz o `setState` cair num componente desmontado.
    let ativo = true;
    setCarregando(true);
    setErro(null);
    setSemPermissao(false);

    auditoriaService
      .listar({ page: paginaAlvo, limit: POR_PAGINA })
      .then((resposta) => {
        if (!ativo) return;
        setEventos(resposta?.dados ?? []);
        setTotal(resposta?.paginacao?.total ?? 0);
        setCobertura(resposta?.cobertura ?? []);
        setDesde(resposta?.desde ?? null);
        setEstrutura(resposta?.estrutura);
        setFiltrosIgnorados(resposta?.filtrosIgnorados ?? []);
        setCarregando(false);
      })
      .catch((erroDaRede) => {
        if (!ativo) return;
        setEventos([]);
        setFiltrosIgnorados([]);
        setCarregando(false);
        if (erroDaRede?.response?.status === 403) {
          setSemPermissao(true);
          return;
        }
        setErro(
          erroDaRede?.response?.data?.message ??
            "Não foi possível carregar o log de auditoria."
        );
      });

    return () => {
      ativo = false;
    };
  }, []);

  useEffect(() => carregar(pagina), [carregar, pagina]);

  const paginas = Math.max(1, Math.ceil(total / POR_PAGINA));

  // Nome da loja a partir do InfoToken. O log grava o uuid (é ele que sobrevive
  // à loja ser renomeada); o nome é enfeite de leitura, e quando ele não estiver
  // no token a tela mostra o uuid — nunca inventa.
  const nomeDaLoja = useMemo(() => {
    const mapa = new Map<string, string>();
    const empresas = decryptedInfoToken?.organization?.companies ?? [];
    empresas.forEach((empresa: any) =>
      (empresa?.subsidiaries ?? []).forEach((filial: any) => {
        if (filial?.subsidiaryUuid && filial?.subsidiaryName) {
          mapa.set(filial.subsidiaryUuid, filial.subsidiaryName);
        }
      })
    );
    return mapa;
  }, [decryptedInfoToken]);

  const onde = (evento: EventoDeAuditoria) => {
    if (!evento.subsidiaryUuid) return "—";
    return nomeDaLoja.get(evento.subsidiaryUuid) ?? evento.subsidiaryUuid;
  };

  const quem = (evento: EventoDeAuditoria) =>
    evento.atorNome || evento.atorUuid || "—";

  const objeto = (evento: EventoDeAuditoria) => {
    const tipo = rotuloDaEntidade(evento.entidade);
    return evento.entidadeRotulo ? `${tipo} · ${evento.entidadeRotulo}` : tipo;
  };

  const cabecalho = (
    <AlfredoStyles.Header>
      <AlfredoStyles.HeaderTitle>
        <AlfredoStyles.MaterialIcons>history</AlfredoStyles.MaterialIcons>
        Logs
      </AlfredoStyles.HeaderTitle>
      <AlfredoStyles.HeaderSubtitle>
        O registro do que acontece no sistema — quem mudou o quê, e quando.
      </AlfredoStyles.HeaderSubtitle>
    </AlfredoStyles.Header>
  );

  // O filtro que o servidor recebeu e NÃO conseguiu usar. Fica acima da lista
  // porque é sobre a lista: o que está sendo mostrado não é o recorte pedido.
  const avisoDeFiltroIgnorado =
    filtrosIgnorados.length > 0 ? (
      <AlfredoStyles.Aviso $tom="atencao">
        <strong>Parte do filtro não foi aplicada.</strong>
        <ul>
          {filtrosIgnorados.map((aviso) => (
            <li key={aviso}>{aviso}</li>
          ))}
        </ul>
      </AlfredoStyles.Aviso>
    ) : null;

  // O que É registrado, montado a partir da resposta do servidor. É este bloco
  // que faz o vazio ser honesto em vez de silencioso.
  const listaDaCobertura =
    cobertura.length > 0 ? (
      <AlfredoStyles.Aviso $tom="atencao">
        <strong>O que passa a aparecer aqui.</strong>{" "}
        {desde
          ? `O registro começou em ${comoDataHora(desde)} e cobre:`
          : "O log passou a existir agora e registra estas ações:"}
        <ul>
          {cobertura.map((item) => (
            <li key={item.entidade}>
              {item.rotulo} — {item.acoes.join(", ")}
            </li>
          ))}
        </ul>
        Ação fora desta lista <strong>ainda não é registrada</strong>: a linha
        não estar aqui não quer dizer que nada aconteceu.
      </AlfredoStyles.Aviso>
    ) : null;

  const corpo = () => {
    if (carregando) {
      return (
        <AlfredoStyles.Vazio>
          <AlfredoStyles.MaterialIcons>
            hourglass_top
          </AlfredoStyles.MaterialIcons>
          Carregando o log…
        </AlfredoStyles.Vazio>
      );
    }

    if (semPermissao) {
      return (
        <AlfredoStyles.Aviso $tom="atencao">
          <strong>O log de auditoria é visível apenas para administradores.</strong>{" "}
          Isto não é uma falha: o registro mostra quem fez o quê no sistema, e o
          servidor só o abre para quem administra a rede. Peça a um administrador
          se precisar consultar uma alteração.
        </AlfredoStyles.Aviso>
      );
    }

    if (erro) {
      return (
        <AlfredoStyles.Aviso $tom="erro">
          <strong>Não foi possível carregar.</strong> {erro}
        </AlfredoStyles.Aviso>
      );
    }

    // A TABELA NÃO EXISTE no banco. Não é o mesmo que estar vazia, e a diferença
    // é exatamente a que faz esta tela ser confiável: aqui NADA está sendo
    // gravado, então listar "o que passa a aparecer" seria promessa falsa.
    if (estrutura === "ausente") {
      return (
        <>
          {avisoDeFiltroIgnorado}
          <AlfredoStyles.Vazio>
            <AlfredoStyles.MaterialIcons>
              report_problem
            </AlfredoStyles.MaterialIcons>
            O log não está sendo gravado neste ambiente.
          </AlfredoStyles.Vazio>
          <AlfredoStyles.Aviso $tom="erro">
            <strong>A estrutura do log não existe neste banco.</strong> A tabela
            de auditoria é criada quando o servidor sobe, e nesta instalação essa
            preparação não completou — por isso não há o que mostrar, e{" "}
            <strong>nenhuma ação está sendo registrada agora</strong>. Isto não
            é "nada aconteceu": é o registro desligado. O Central segue
            funcionando (a falha do log nunca derruba uma operação); quem
            administra o servidor consegue confirmar em <code>GET /saude</code>,
            no campo <code>auditoria</code>.
          </AlfredoStyles.Aviso>
        </>
      );
    }

    if (eventos.length === 0) {
      return (
        <>
          {avisoDeFiltroIgnorado}
          <AlfredoStyles.Vazio>
            <AlfredoStyles.MaterialIcons>
              history_toggle_off
            </AlfredoStyles.MaterialIcons>
            {filtrosIgnorados.length > 0
              ? "Nenhuma ação registrada no que foi possível consultar."
              : "Nenhuma ação registrada ainda."}
          </AlfredoStyles.Vazio>
          {listaDaCobertura}
        </>
      );
    }

    return (
      <>
        {avisoDeFiltroIgnorado}
        <AlfredoStyles.Tabela>
          <thead>
            <tr>
              <th>Quando</th>
              <th>Quem</th>
              <th>Ação</th>
              <th>Objeto</th>
              <th>Onde</th>
            </tr>
          </thead>
          <tbody>
            {eventos.map((evento) => (
              <tr key={evento.auditoriaUuid}>
                <td>{comoDataHora(evento.ocorridoEm)}</td>
                <td>{quem(evento)}</td>
                <td>
                  <AlfredoStyles.Selo $ativo={evento.resultado === "sucesso"}>
                    {ROTULO_DA_ACAO[evento.acao] ?? evento.acao}
                  </AlfredoStyles.Selo>
                </td>
                <td>{objeto(evento)}</td>
                <td>{onde(evento)}</td>
              </tr>
            ))}
          </tbody>
        </AlfredoStyles.Tabela>

        <AlfredoStyles.Paginacao>
          <AlfredoStyles.Button
            onClick={() => setPagina((p) => Math.max(1, p - 1))}
            disabled={pagina <= 1}
          >
            <AlfredoStyles.MaterialIcons>
              chevron_left
            </AlfredoStyles.MaterialIcons>
            Anterior
          </AlfredoStyles.Button>
          <span>
            Página {pagina} de {paginas} · {total} registro(s)
          </span>
          <AlfredoStyles.Button
            onClick={() => setPagina((p) => Math.min(paginas, p + 1))}
            disabled={pagina >= paginas}
          >
            Próxima
            <AlfredoStyles.MaterialIcons>
              chevron_right
            </AlfredoStyles.MaterialIcons>
          </AlfredoStyles.Button>
        </AlfredoStyles.Paginacao>

        {listaDaCobertura}
      </>
    );
  };

  return (
    <AlfredoStyles.Container>
      {cabecalho}
      {corpo()}

      {/* As trilhas VIZINHAS, que não são este log. Elas continuam citadas
          porque o estado vazio anterior as nomeava, e apagar essa informação
          agora faria a tela parecer mais completa do que é. */}
      <AlfredoStyles.Aviso $tom="ok">
        <strong>Outras trilhas, fora deste log.</strong>{" "}
        asup.&quot;MensagemSaida&quot; guarda o que sairia para fornecedor
        (destinatário, canal, texto exato, hora e estado) e tem tela própria em
        Comunicações › Log de envio. asite.&quot;HandleAuditoria&quot; guarda o
        histórico da troca de endereço do site e acfg.&quot;AlinhamentoLog&quot;
        guarda as execuções do alinhador de configuração — nenhuma das duas tem
        rota que as exponha. Trocas de endereço feitas de agora em diante
        aparecem também aqui.
      </AlfredoStyles.Aviso>
    </AlfredoStyles.Container>
  );
};

export default LogsDeAuditoria;
